import React, { useState, useCallback } from 'react';
import {
  Search,
  X,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CircleCheck as CheckCircle,
  Loader as Loader2,
  Clock,
  User,
  FileText,
  ShieldCheck,
  MessageSquare,
  MessageSquareText,
  ChevronDown,
  ChevronUp,
  Inbox,
  Save,
  Hash,
} from 'lucide-react';
import { useCustomerComplaints } from '../../hooks/useCustomerComplaints';
import { ResolveNoteOverlay } from './ResolveNoteOverlay';
import { Button } from '../ui/Button';
import type { CustomerComplaint } from '../../types/customerComplaints';
import type { ShopifyOrderLookupResult } from '../../types/customerComplaints';

interface CustomerComplaintSectionProps {
  mode: 'create' | 'resolved';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ──────────────── Complaint Card ──────────────── */

interface ComplaintCardProps {
  complaint: CustomerComplaint;
  isResolved: boolean;
  onResolve?: (id: string) => void;
}

const ComplaintCard: React.FC<ComplaintCardProps> = ({ complaint, isResolved, onResolve }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 transition-all hover:shadow-md">
      <div className="flex items-start gap-4 p-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isResolved
            ? 'bg-emerald-100 dark:bg-emerald-900/30'
            : 'bg-amber-100 dark:bg-amber-900/30'
        }`}>
          {isResolved ? (
            <MessageSquareText className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <MessageSquare className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
              {complaint.order_reference}
            </span>
            {complaint.customer_name && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                {complaint.customer_name}
              </span>
            )}
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
              isResolved
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            }`}>
              {isResolved ? 'Resolved' : 'Open'}
            </span>
          </div>

          <p className={`mt-1.5 text-sm text-gray-700 dark:text-gray-300 ${expanded ? '' : 'line-clamp-2'}`}>
            {complaint.complaint_text}
          </p>

          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatShortDate(complaint.created_at)}
            </span>
            {complaint.created_by_name && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {complaint.created_by_name}
              </span>
            )}
          </div>

          {isResolved && complaint.resolved_at && (
            <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/30 w-fit">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                Resolved {formatDate(complaint.resolved_at)}
              </span>
              {complaint.resolved_by_name && (
                <>
                  <span className="text-emerald-300 dark:text-emerald-600">|</span>
                  <User className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                    {complaint.resolved_by_name}
                  </span>
                </>
              )}
            </div>
          )}

          {isResolved && complaint.resolution_note && (
            <div className="flex items-start gap-2 mt-2 px-2.5 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-100 dark:border-gray-600/30 w-fit max-w-full">
              <FileText className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-gray-600 dark:text-gray-300 break-words">
                {complaint.resolution_note}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {!isResolved && onResolve && (
            <button
              onClick={() => onResolve(complaint.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 shadow-sm shadow-emerald-500/20 transition-all"
              title="Resolve"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Resolve
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ──────────────── Create Complaint Form ──────────────── */

interface CreateFormProps {
  onCreated: () => void;
}

const CreateComplaintForm: React.FC<CreateFormProps> = ({ onCreated }) => {
  const { createComplaint, lookupOrder } = useCustomerComplaints('open');

  const [orderRef, setOrderRef] = useState('');
  const [lookupResults, setLookupResults] = useState<ShopifyOrderLookupResult[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ShopifyOrderLookupResult | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupDone, setLookupDone] = useState(false);
  const [complaintText, setComplaintText] = useState('');
  const [saving, setSaving] = useState(false);

  const handleLookup = useCallback(async () => {
    if (!orderRef.trim()) return;
    setLookingUp(true);
    setLookupDone(false);
    setSelectedOrder(null);
    const results = await lookupOrder(orderRef.trim());
    setLookupResults(results);
    if (results.length === 1) {
      setSelectedOrder(results[0]);
    }
    setLookupDone(true);
    setLookingUp(false);
  }, [orderRef, lookupOrder]);

  const handleSelectOrder = useCallback((result: ShopifyOrderLookupResult) => {
    setSelectedOrder(result);
    setOrderRef(result.order_number);
  }, []);

  const handleSave = useCallback(async () => {
    if (!orderRef.trim() || !complaintText.trim()) return;
    setSaving(true);
    try {
      await createComplaint(
        selectedOrder?.order_number || orderRef.trim(),
        selectedOrder?.shopify_order_id ?? null,
        selectedOrder?.customer_name ?? null,
        complaintText.trim(),
      );
      setOrderRef('');
      setComplaintText('');
      setSelectedOrder(null);
      setLookupResults([]);
      setLookupDone(false);
      onCreated();
    } catch {
      // error logged in hook
    } finally {
      setSaving(false);
    }
  }, [orderRef, complaintText, selectedOrder, createComplaint, onCreated]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-5 border-b border-gray-100 dark:border-gray-700/50">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20 flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              New Complaint
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Search the order reference to auto-fill customer info, then describe the complaint
            </p>
          </div>
        </div>

        {/* Order Lookup */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Order Reference Number
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="e.g. #1001 or 1001"
                value={orderRef}
                onChange={(e) => {
                  setOrderRef(e.target.value);
                  setLookupDone(false);
                  setSelectedOrder(null);
                  setLookupResults([]);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all placeholder:text-gray-400"
              />
            </div>
            <button
              onClick={handleLookup}
              disabled={lookingUp || !orderRef.trim()}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-semibold hover:from-blue-600 hover:to-indigo-700 shadow-sm shadow-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {lookingUp ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </button>
          </div>

          {/* Lookup Results */}
          {lookupDone && lookupResults.length === 0 && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              No matching Shopify order found. You can still enter the order reference manually.
            </p>
          )}

          {lookupDone && lookupResults.length > 1 && !selectedOrder && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Multiple orders found -- select one:
              </p>
              {lookupResults.map((r) => (
                <button
                  key={r.shopify_order_id}
                  onClick={() => handleSelectOrder(r)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors text-left"
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{r.order_number}</span>
                  {r.customer_name && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">{r.customer_name}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {selectedOrder && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40">
              <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Customer: {selectedOrder.customer_name || 'Unknown'}
              </span>
              <span className="text-xs text-emerald-500 dark:text-emerald-400 ml-auto">
                {selectedOrder.order_number}
              </span>
            </div>
          )}

          {/* Complaint Text */}
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-4">
            Complaint Description
          </label>
          <textarea
            value={complaintText}
            onChange={(e) => setComplaintText(e.target.value)}
            rows={4}
            placeholder="Describe the customer complaint in detail..."
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none transition-all placeholder:text-gray-400"
          />

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !orderRef.trim() || !complaintText.trim()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-semibold hover:from-amber-600 hover:to-orange-700 shadow-sm shadow-amber-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Complaint
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ──────────────── Main Section ──────────────── */

export const CustomerComplaintSection: React.FC<CustomerComplaintSectionProps> = ({ mode }) => {
  const statusFilter = mode === 'create' ? 'open' : 'resolved';
  const {
    complaints,
    loading,
    total,
    page,
    pageSize,
    search,
    sortDirection,
    setPage,
    updateSearch,
    toggleSort,
    loadComplaints,
    resolveComplaint,
  } = useCustomerComplaints(statusFilter);

  const [refreshing, setRefreshing] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<CustomerComplaint | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const isResolved = mode === 'resolved';

  const headerConfig = isResolved
    ? {
        title: 'Customer Resolve Complaint',
        subtitle: 'Resolved customer complaints with full audit trail',
        gradient: 'bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/20',
        icon: MessageSquareText,
        badgeBg: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
        emptyTitle: 'No resolved complaints',
        emptySubtitle: 'When you resolve open complaints, they will appear here.',
        emptyIconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
        emptyIconColor: 'text-emerald-500',
      }
    : {
        title: 'Customer Complaint',
        subtitle: 'Open customer complaints pending resolution',
        gradient: 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/20',
        icon: MessageSquare,
        badgeBg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
        emptyTitle: 'No open complaints',
        emptySubtitle: 'Create a new complaint above using the order reference form.',
        emptyIconBg: 'bg-amber-100 dark:bg-amber-900/30',
        emptyIconColor: 'text-amber-500',
      };

  const HeaderIcon = headerConfig.icon;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadComplaints();
    setRefreshing(false);
  }, [loadComplaints]);

  const handleResolveClick = useCallback(
    (complaintId: string) => {
      const c = complaints.find((x) => x.id === complaintId);
      if (c) setResolveTarget(c);
    },
    [complaints],
  );

  const handleResolveConfirm = useCallback(
    async (note?: string) => {
      if (!resolveTarget) return;
      await resolveComplaint(resolveTarget.id, note);
      setResolveTarget(null);
    },
    [resolveTarget, resolveComplaint],
  );

  return (
    <div className="space-y-5">
      {/* Create Form (only in create mode) */}
      {mode === 'create' && <CreateComplaintForm onCreated={loadComplaints} />}

      {/* Complaints List */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-5 border-b border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${headerConfig.gradient}`}>
                <HeaderIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {headerConfig.title}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {headerConfig.subtitle}
                </p>
              </div>
              {!loading && (
                <span className={`ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${headerConfig.badgeBg}`}>
                  {total}
                </span>
              )}
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 text-gray-600 dark:text-gray-400 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Search & Sort */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order number or customer name..."
                value={search}
                onChange={(e) => updateSearch(e.target.value)}
                className="w-full pl-10 pr-8 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
              />
              {search && (
                <button
                  onClick={() => updateSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <button
              onClick={toggleSort}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
            >
              {sortDirection === 'desc' ? (
                <ArrowDown className="h-4 w-4" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
              {sortDirection === 'desc' ? 'Newest First' : 'Oldest First'}
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
          </div>
        ) : complaints.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${headerConfig.emptyIconBg}`}>
              <Inbox className={`h-8 w-8 ${headerConfig.emptyIconColor}`} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              {headerConfig.emptyTitle}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm">
              {headerConfig.emptySubtitle}
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {complaints.map((c) => (
              <ComplaintCard
                key={c.id}
                complaint={c}
                isResolved={isResolved}
                onResolve={!isResolved ? handleResolveClick : undefined}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-gray-700/50">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        )}

        {/* Resolve Overlay */}
        {resolveTarget && (
          <ResolveNoteOverlay
            orderReference={resolveTarget.order_reference}
            onConfirm={handleResolveConfirm}
            onClose={() => setResolveTarget(null)}
          />
        )}
      </div>
    </div>
  );
};
